import React, { Suspense, lazy, useEffect, ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import ScrollToTop from "./ScrollToTop";
import Footer1 from "shared/footer1/Footer1";
import SiteHeader from "containers/SiteHeader";

import { CartProvider } from "containers/PageCheckout/CartContext";
import { AuthProvider, useAuth } from "../api/AuthContext";
import { getTracker } from "utils/analytics/tracker";
import ThemeLoader from "components/Shared/ThemeLoader";

import { Page } from "./types";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const PageHome = lazy(() => import("containers/PageHome/PageHome"));
const PageSearch = lazy(() => import("containers/PageSearch"));
const Page404 = lazy(() => import("containers/Page404/Page404"));
const AccountPage = lazy(() => import("containers/AccountPage/AccountPage"));
const PageContact = lazy(() => import("containers/PageContact/PageContact"));
const PageAbout = lazy(() => import("containers/PageAbout/PageAbout"));
const ForgotPassword = lazy(() => import("containers/ForgotPassword/ForgotPassword"));
const ResetPassword = lazy(() => import("containers/ResetPassword/ResetPassword"));
const PageSubcription = lazy(() => import("containers/PageSubcription/PageSubcription"));
const Filaments = lazy(() => import("containers/Filaments"));
const Accessories = lazy(() => import("containers/Accessories"));
const ServiceParts = lazy(() => import("containers/ServiceParts"));
const Printers = lazy(() => import("containers/3dprinter"));
const RoboticsPage = lazy(() => import("containers/RoboticsPage"));
const ProductDetailPage = lazy(() => import("containers/ProductDetailPage/ProductDetailPage"));
const AccountSavelists = lazy(() => import("containers/AccountPage/AccountSavelists"));
const AccountPass = lazy(() => import("containers/AccountPage/AccountPass"));
const AccountBilling = lazy(() => import("containers/AccountPage/AccountBilling"));
const AccountOrder = lazy(() => import("containers/AccountPage/AccountOrder"));
const CartPage = lazy(() => import("containers/ProductDetailPage/CartPage"));
const CheckoutPage = lazy(() => import("containers/PageCheckout/CheckoutPage"));
const OrderDetails = lazy(() => import("containers/AccountPage/OrderDetails"));
const PageOffers = lazy(() => import("containers/PageOffers/PageOffers"));
const PageAuth = lazy(() => import("containers/PageAuth/PageAuth"));
const PrivacyPolicy = lazy(() => import("containers/Privacy/PrivacyPolicy"));
const Terms = lazy(() => import("containers/Privacy/Terms"));
const Warranty = lazy(() => import("containers/Privacy/Warranty"));
const PageBuyersGuide = lazy(() => import("containers/PageBuyersGuide/PageBuyersGuide"));

const RouteFallback = () => (
  <div className="container py-24 flex flex-col items-center justify-center">
    <ThemeLoader size="lg" />
  </div>
);

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <RouteFallback />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

const isProtectedPath = (path: string) => {
  // Account + order history/details should only be accessible to logged-in users.
  return path.startsWith("/account") || path.startsWith("/order/");
};

export const pages: Page[] = [
  { path: "/", component: PageHome },
  { path: "/product/:id", component: ProductDetailPage },
  { path: "/product/robotics/:id", component: ProductDetailPage },
  { path: "/printers", component: Printers },
  { path: "/filaments", component: Filaments },
  { path: "/accessories", component: Accessories },
  { path: "/spare-parts", component: ServiceParts },
  { path: "/shop", component: PageSearch },
  
  // Robotics Routes
  { 
    path: "/robotics", 
    component: () => (
      <PageSearch 
        initialCategory="All Robotics"
      />
    )
  },
  { 
    path: "/robotics-arms", 
    component: () => (
      <RoboticsPage 
        categoryName="Robotic Arms"
        categoryTitle="Collaborative Robotic Arms"
        categoryDescription="Explore the myCobot, mechArm, and myPalletizer series. Versatile cobots for research, education, and light industrial tasks."
        heroImage="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2670&auto=format&fit=crop"
      />
    )
  },
  { 
    path: "/robotics-pets", 
    component: () => (
      <RoboticsPage 
        categoryName="Robotic Pets"
        categoryTitle="AI Robotic Companions"
        categoryDescription="Meet MarsCat and MetaCat. Intelligent, autonomous, and fully programmable robotic pets powered by advanced AI."
        heroImage="https://images.unsplash.com/photo-1531746790731-6c087fecd05a?q=80&w=2506&auto=format&fit=crop"
      />
    )
  },
  { 
    path: "/robotics-humanoids", 
    component: () => (
      <RoboticsPage 
        categoryName="Mercury"
        categoryTitle="Humanoid & Professional Robots"
        categoryDescription="Experience the Mercury Series. High-performance humanoid robots designed for embodied AI research and complex automation."
        heroImage="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2670&auto=format&fit=crop"
      />
    )
  },
  { 
    path: "/robotics-accessories", 
    component: () => (
      <RoboticsPage 
        categoryName="Robotics Accessories"
        categoryTitle="Robotics Parts & AI Kits"
        categoryDescription="Complete your setup with grippers, suction pumps, camera modules, and comprehensive AI kits for all robot series."
        heroImage="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2670&auto=format&fit=crop"
      />
    )
  },

  { path: "/page-search", component: PageSearch },
  { path: "/account", component: AccountPage },
  { path: "/account-savelists", component: AccountSavelists },
  { path: "/account-change-password", component: AccountPass },
  { path: "/account-billing", component: AccountBilling },
  { path: "/account-my-order", component: AccountOrder },
  { path: "/order/:id", component: OrderDetails },
  { path: "/cart", component: CartPage },
  { path: "/checkout", component: CheckoutPage },
  { path: "/contact", component: PageContact },
  { path: "/about", component: PageAbout },
  { path: "/login", component: PageAuth },
  { path: "/signup", component: () => <PageAuth initialView="signup" /> },
  { path: "/verify-email", component: () => <PageAuth initialView="verify" /> },
  { path: "/forgot-pass", component: ForgotPassword },
  { path: "/reset-pass", component: ResetPassword },
  { path: "/subscription", component: PageSubcription },
  { path: "/offers", component: PageOffers },
  { path: "/terms-n-conditions", component: Terms },
  { path: "/warranty", component: Warranty },
  { path: "/privacy-policy", component: PrivacyPolicy},
  { path: "/buyers-guide", component: PageBuyersGuide },
];

// Define paths that should NOT have the header/footer
const AUTH_PATHS = ["/login", "/signup", "/forgot-pass", "/verify-email", "/reset-pass"];

const MyRoutes = () => {
  const location = useLocation();
  const isAuthPage = AUTH_PATHS.includes(location.pathname);

  // Track page views on route change
  useEffect(() => {
    const tracker = getTracker();
    if (tracker) {
      tracker.trackPageView();
    }
  }, [location.pathname]);

  return (
    <AuthProvider>
      <CartProvider>
        <ScrollToTop />
        {!isAuthPage && <SiteHeader />} 
        <Suspense fallback={<RouteFallback />}>
          <SentryRoutes>
            {pages.map(({ component: Component, path }, index) => {
              const element = isProtectedPath(path) ? (
                <RequireAuth>
                  <Component />
                </RequireAuth>
              ) : (
                <Component />
              );

              return <Route key={index} path={path} element={element} />;
            })}
            <Route path="/Printers" element={<Navigate to="/printers" replace />} />
            <Route path="*" element={<Page404 />} />
          </SentryRoutes>
        </Suspense>
        {!isAuthPage && <Footer1 />} 
      </CartProvider>
    </AuthProvider>
  );
};

export default MyRoutes;
